/**
 * ZAHLUNGSAVISE DER TELEKOM (.481), nur mit abrechnung.geld.
 *
 * ORIGINALTEXT MARVIN (23.10.2026)
 *   „wir müssen Gutschrift, Zahlungserwies gegenprüfen."
 * Je Avis die Posten mit Gutschrift, Baustelle, Brutto, Abzug, erwartetem
 * Abzug laut Vertrag und dem Hinweis, wenn etwas nicht passt.
 */
import { useState } from 'react';
import { api, datum, euro } from '../api.ts';
import { Karte, Leer } from './Geruest.tsx';
import { Dialog, Fehlerkasten, ausFehler, type Fehlerlage } from './Formular.tsx';
import { useMerkDaten } from './merkplatz.ts';

export function Zahlungsavise() {
  const [fehler, setFehler] = useState<Fehlerlage>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [d, , nachladen] = useMerkDaten<{ avise: any[] }>('telekom.avise', () => api.telekomAvise(), (f) => setFehler(ausFehler(f)));
  const tu = async (f: () => Promise<{ klartext: string }>) => {
    setLaeuft(true); setFehler(null); setHinweis(null);
    try { const r = await f(); setHinweis(r.klartext); await nachladen(); } catch (x) { setFehler(ausFehler(x)); } finally { setLaeuft(false); }
  };
  const hochladen = (datei: File) => tu(async () => {
    const b = new Uint8Array(await datei.arrayBuffer());
    let s = ''; for (let i = 0; i < b.length; i += 0x8000) s += String.fromCharCode(...b.subarray(i, i + 0x8000));
    return api.telekomAvisHochladen(btoa(s));
  });
  const avise = d?.avise ?? [];
  /* BEANSTANDEN (.482): Posten wählen, Schreiben ansehen, als Entwurf ins Postfach. */
  const [wahl, setWahl] = useState<Record<string, Set<string>>>({});
  const [schreiben, setSchreiben] = useState<{ avisId: string; posten: string[]; betreff: string; text: string; summe: number; an: string; an_herkunft: string; konten: any[] } | null>(null);
  const [an, setAn] = useState('');
  const kopieren = (was: string, wort: string) => { void navigator.clipboard?.writeText(was); setHinweis(`${wort} kopiert.`); };
  const [konto, setKonto] = useState('');
  const gewaehlt = (a: any) => wahl[a.id] ?? new Set(a.posten.filter((p: any) => p.nach_frist && !p.beanstandet_am).map((p: any) => p.id));
  const umschalten = (a: any, id: string, an: boolean) => setWahl((w) => { const n = new Set(gewaehlt(a)); if (an) n.add(id); else n.delete(id); return { ...w, [a.id]: n }; });
  const oeffnen = async (a: any) => {
    setFehler(null);
    try { const ids = [...gewaehlt(a)]; const r = await api.telekomBeanstandung(a.id, ids); setKonto(r.konten[0]?.id ?? ''); setAn(r.an ?? ''); setSchreiben({ ...r, avisId: a.id, posten: ids }); /* .491: r.posten ist eine Zahl und überschrieb die Auswahl (s.join is not a function). */ }
    catch (x) { setFehler(ausFehler(x)); }
  };
  return (
    <div className="inhalt">
      <Karte titel={`Zahlungsavise der Telekom (${avise.length})`}
             rechts={<div className="reihe" style={{ gap: 8 }}>
               <button className="knopf klein haupt" disabled={laeuft} onClick={() => tu(() => api.telekomAviseLesen())}>{laeuft ? 'Einen Moment' : 'Jetzt aus den Postfächern lesen'}</button>
               <label className="knopf klein">PDF hochladen<input type="file" accept="application/pdf" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) hochladen(f); e.target.value = ''; }} /></label>
             </div>}>
        <Fehlerkasten lage={fehler} />
        {hinweis && <div className="hinweis">{hinweis}</div>}
        <p className="klein leise" style={{ marginTop: 0 }}>
          Gelesen werden die Mails von Aviseversand@telekom.de in allen Ordnern der Postfächer, stündlich. Jeder Posten wird seiner Gutschrift zugeordnet;
          geprüft wird der Abzug gegen den Rahmenvertrag (4650020458: 3 % Skonto, 14 Tage). Die 14 Tage zählen ab dem Datum der Gutschrift, der Tag selbst zählt nicht mit;
          das „Belegdatum" im Avis ist nur das Datum der Referenz und kommt einige Tage früher.
        </p>
        {d && !avise.length && <Leer text="Noch kein Zahlungsavis gelesen." />}
        {avise.map((a) => (
          <details key={a.id} open={a.befunde > 0} className="zahlgruppe" style={{ marginTop: 10 }}>
            <summary>
              <b>Avis {a.nummer}</b> vom {datum(a.datum)}, {euro(a.summe_brutto)} brutto, Abzug {euro(a.summe_abzug)}
              {a.befunde > 0 ? <span className="merker fehler klein" style={{ marginLeft: 8 }}>{a.befunde} mit Hinweis</span> : <span className="merker gruen klein" style={{ marginLeft: 8 }}>passt</span>}
              {!a.stimmt && <span className="merker fehler klein" style={{ marginLeft: 8 }}>Summe nicht nachgerechnet</span>}
            </summary>
            <table className="liste" style={{ marginTop: 6 }}>
              <thead><tr><th></th><th>Gutschrift</th><th>Gutschrift vom</th><th className="zahl">bezahlt nach</th><th>Baustelle</th><th className="zahl">Brutto</th><th className="zahl">Abzug</th><th className="zahl">laut Vertrag</th><th>Hinweis</th></tr></thead>
              <tbody>
                {a.posten.map((p: any) => (
                  <tr key={p.beleg} className={p.befund ? 'ungeprueft' : ''}>
                    <td>{p.abzug > 0 && p.nach_frist && <input type="checkbox" checked={gewaehlt(a).has(p.id)} onChange={(e) => umschalten(a, p.id, e.target.checked)} aria-label={`Posten ${p.referenz} beanstanden`} />}</td>
                    <td>{p.gutschrift ?? p.referenz}{!p.gutschrift && <div className="klein fehlertext">nicht bekannt</div>}</td>
                    <td>{p.gutschrift_datum ? datum(p.gutschrift_datum) : <span className="leise">unbekannt</span>}<div className="klein leise">Referenz vom {datum(p.belegdatum)}</div></td>
                    <td className="zahl">{p.tage != null ? `${p.tage} Tg.` : ''}</td>
                    <td>{p.auftrag_id && p.anschrift ? <a href={`#/akte/${p.auftrag_id}`}>{p.anschrift}</a> : (p.kls ? `KLS ${p.kls}` : '')}</td>
                    <td className="zahl">{euro(p.brutto)}</td>
                    <td className="zahl">{euro(p.abzug)}</td>
                    <td className="zahl">{p.vertrag ? euro(p.erwartet) : <span className="leise">kein Vertrag</span>}</td>
                    <td className={p.befund ? 'fehlertext klein' : 'klein leise'}>{p.befund ?? (p.abzug > 0 && p.tage != null ? `passt: Skonto in der Frist (${p.tage} Tage nach der Gutschrift)` : 'passt')}
                      {p.beanstandet_am && !p.beanstandung_hinfaellig_am && <div className="leise">beanstandet am {datum(p.beanstandet_am)}</div>}
                      {p.beanstandung_hinfaellig_am && <div className="leise">beanstandet am {datum(p.beanstandet_am)}, hinfällig: ab dem Datum der Gutschrift gerechnet war die Zahlung pünktlich. Beanstandung bei der Telekom zurückziehen.</div>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {a.posten.some((p: any) => p.abzug > 0 && p.nach_frist) && (
              <div className="reihe" style={{ gap: 8, marginTop: 6 }}>
                <button type="button" className="knopf klein" disabled={!gewaehlt(a).size} onClick={() => oeffnen(a)}>Beanstandung erzeugen ({gewaehlt(a).size})</button>
              </div>
            )}
          </details>
        ))}
      </Karte>
      {schreiben && (
        <Dialog titel="Beanstandung an die Telekom" breite={760} zu={() => setSchreiben(null)}>
          <p className="klein leise" style={{ marginTop: 0 }}>Die Plattform sendet nichts an die Telekom. „Mail für Outlook herunterladen“, anklicken, Absender wählen und senden. Deine Outlook-Signatur kommt von selbst dazu.</p>
          {hinweis && <div className="hinweis">{hinweis}</div>}
          <div className="reihe" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <b style={{ minWidth: 60 }}>An:</b>
            <input type="email" value={an} onChange={(e) => setAn(e.target.value)} placeholder="Mailadresse der Telekom" style={{ flex: 1, minWidth: 220 }} />
            <button type="button" className="knopf klein" disabled={!an} onClick={() => kopieren(an, 'Empfänger')}>Kopieren</button>
          </div>
          <div className="klein leise" style={{ marginLeft: 68 }}>{an && an !== schreiben.an ? 'von Hand eingetragen, wird gemerkt' : schreiben.an_herkunft}</div>
          <div className="reihe" style={{ gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
            <b style={{ minWidth: 60 }}>Betreff:</b>
            <span style={{ flex: 1 }}>{schreiben.betreff}</span>
            <button type="button" className="knopf klein" onClick={() => kopieren(schreiben.betreff, 'Betreff')}>Kopieren</button>
          </div>
          <textarea readOnly value={schreiben.text} rows={16} style={{ width: '100%', marginTop: 8, fontFamily: 'inherit' }} />
          <div className="reihe" style={{ gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <a className="knopf klein haupt" href={api.telekomBeanstandungEml(schreiben.avisId, schreiben.posten, an)} download
               onClick={() => { setTimeout(() => { void nachladen(); }, 1500); setHinweis('Mail heruntergeladen: in Outlook öffnen und senden. Die Posten gelten als beanstandet.'); }}>Mail für Outlook herunterladen</a>
            <button type="button" className="knopf klein" onClick={() => kopieren(schreiben.text, 'Text')}>Text kopieren</button>
            <select value={konto} onChange={(e) => setKonto(e.target.value)}>
              {schreiben.konten.map((k: any) => <option key={k.id} value={k.id}>{k.adresse ?? k.name}</option>)}
            </select>
            <button type="button" className="knopf klein" disabled={laeuft || !konto}
                    onClick={() => { const s = schreiben; setSchreiben(null); void tu(() => api.telekomBeanstandungEntwurf(s.avisId, s.posten, konto, an)); }}>Als Entwurf ablegen</button>
          </div>
        </Dialog>
      )}
    </div>
  );
}
